import { defineMessages } from "../define";

/**
 * Monitoring board, BiteBerry tab (monitoring.bb.*): what BB sends, what the backend did with it, and why
 * finished pizzas have no photo. Shared words (verdicts, columns) live in monitoring.ts.
 */
export default defineMessages({
  en: {
    "monitoring.tab.bb": "BiteBerry",

    // --- summary -------------------------------------------------------------------------------------
    "monitoring.bb.summary.coverage": "Finished pizzas photographed",
    "monitoring.bb.summary.coverageHint":
      "Pizzas of orders at branches that photograph, finished by BiteBerry (packed, or the order went past the kitchen) in the window (7 days at most)",
    "monitoring.bb.summary.coverageDetail": "{captured} of {finished} · {missing} without a photo",
    "monitoring.bb.summary.packed": "Packing reported",
    "monitoring.bb.summary.packedHint":
      "Pizzas whose item webhook reported the packing step (Balení) done, at branches that photograph",
    "monitoring.bb.summary.packedDetail": "{captured} fired the camera · {missing} did not",
    "monitoring.bb.summary.received": "Webhooks received",
    "monitoring.bb.summary.receivedHint":
      "Every BiteBerry webhook in the window, all branches (counted by the backend instances of the data source)",
    "monitoring.bb.summary.receivedDetail": "{orders} orders · {items} items · {statuses} statuses",
    "monitoring.bb.summary.unmatched": "Items without an order",
    "monitoring.bb.summary.unmatchedHint":
      "Pizza item webhooks whose order we did not have stored when they came: a lost order, an item webhook ahead of its order, or a branch without a camera whose order this instance has not seen",
    "monitoring.bb.summary.unmatchedDetail": "distinct item ids",
    "monitoring.bb.summary.other": "Branches without a camera",
    "monitoring.bb.summary.otherHint": "Branches that send orders, with photos off (enable_acquisition)",
    "monitoring.bb.summary.otherDetail": "{orders} orders",
    "monitoring.bb.summary.log": "Webhook log",
    "monitoring.bb.summary.logHint":
      "Logged webhooks are kept {days} days. Older pizzas are explained from their stored flags only.",
    "monitoring.bb.summary.logKept": "first webhook · kept {days} days",
    "monitoring.bb.summary.logEmpty": "nothing logged yet",
    "monitoring.bb.summary.logMemory": "memory of this instance (persistence off)",

    // --- uncaptured ----------------------------------------------------------------------------------
    "monitoring.bb.uncaptured.title": "Finished pizzas without a photo",
    "monitoring.bb.uncaptured.description":
      "Pizzas BiteBerry finished (packed, or the order went past the kitchen) that have no capture time, with the most likely reason from the pizza and every webhook logged for it.",
    "monitoring.bb.uncaptured.empty": "Every finished pizza has a photo.",
    "monitoring.bb.uncaptured.emptyFilter": "No pizza with this reason.",
    "monitoring.bb.uncaptured.all": "All reasons",
    "monitoring.bb.uncaptured.packedAt": "packed",
    "monitoring.bb.uncaptured.orderedAt": "ordered",
    "monitoring.bb.uncaptured.orderStatus": "Order",
    "monitoring.bb.uncaptured.hooks": "Item webhooks",
    "monitoring.bb.uncaptured.packedHooks": "packing done in {count}",
    "monitoring.bb.uncaptured.lastSteps": "Last steps",
    "monitoring.bb.uncaptured.preLog": "before the log",
    "monitoring.bb.uncaptured.preLogHint":
      "The order is older than the webhook log; the reason comes from the pizza's stored flags only",
    "monitoring.bb.uncaptured.hasPhoto": "photo exists",
    "monitoring.bb.uncaptured.timeline": "Timeline",
    "monitoring.bb.uncaptured.more": "Showing {shown} of {total}",

    // --- reasons -------------------------------------------------------------------------------------
    "monitoring.bb.reason.capture_time_lost": "Photo exists, capture time lost",
    "monitoring.bb.reason.capture_time_lost.hint":
      "The camera took the photo and the pizza was evaluated, but capture_at is gone: the pipeline stores the whole pizza document it read before the capture was saved. Lists sorted by capture time miss the pizza.",
    "monitoring.bb.reason.split_item": "Piece of a line ordered more than once",
    "monitoring.bb.reason.split_item.hint":
      "A pizza ordered with quantity 2+ is stored as X:0, X:1, …, but BiteBerry reports packing for the line X. The webhook finds no order and the camera never fires.",
    "monitoring.bb.reason.no_order": "Item webhook without its order",
    "monitoring.bb.reason.no_order.hint":
      "BiteBerry reported packing, but at that moment no stored order contained the item (the item webhook came before the order, or the order was not stored).",
    "monitoring.bb.reason.not_pizza": "Not recognised as a pizza",
    "monitoring.bb.reason.not_pizza.hint": "The menu check rejected the item when its packing was reported.",
    "monitoring.bb.reason.branch_not_found": "Branch not found",
    "monitoring.bb.reason.branch_not_found.hint": "The order's branch is not in the branch list.",
    "monitoring.bb.reason.acquisition_disabled": "Photos off at the branch",
    "monitoring.bb.reason.acquisition_disabled.hint": "The branch had enable_acquisition off when packing was reported.",
    "monitoring.bb.reason.bb_trigger_disabled": "Camera fired by the app",
    "monitoring.bb.reason.bb_trigger_disabled.hint":
      "The branch has the BiteBerry trigger off: the staff take the photo in the app, packing does not fire the camera.",
    "monitoring.bb.reason.no_camera": "No camera at the branch",
    "monitoring.bb.reason.no_camera.hint": "Packing was reported, but no camera is assigned to the branch.",
    "monitoring.bb.reason.trigger_failed": "Camera trigger failed",
    "monitoring.bb.reason.trigger_failed.hint": "The ThingsBoard RPC to the camera failed or did not succeed.",
    "monitoring.bb.reason.device_error": "Camera reported an error",
    "monitoring.bb.reason.device_error.hint": "The camera answered the trigger with an error.",
    "monitoring.bb.reason.error": "Processing error",
    "monitoring.bb.reason.error.hint": "The webhook processing raised an exception (see the detail and the events).",
    "monitoring.bb.reason.capture_not_stored": "Camera fired, capture not stored",
    "monitoring.bb.reason.capture_not_stored.hint":
      "The packed webhook fired the camera, yet the pizza has no capture time and no photo arrived.",
    "monitoring.bb.reason.packed_not_logged": "Packed, webhook not logged",
    "monitoring.bb.reason.packed_not_logged.hint":
      "The pizza is flagged packed, but its packing webhook is not in the log (before the log started, or handled by another instance).",
    "monitoring.bb.reason.never_packed": "Packing never reported",
    "monitoring.bb.reason.never_packed.hint":
      "Item webhooks came (e.g. cooking done), but the packing step (Balení) never reached 0.",
    "monitoring.bb.reason.no_item_webhook": "No item webhook",
    "monitoring.bb.reason.no_item_webhook.hint":
      "No item webhook came for this pizza at all: BiteBerry did not report its steps, or the webhook never arrived.",

    // --- charts ---------------------------------------------------------------------------------------
    "monitoring.bb.chart.volume.title": "Webhooks from BiteBerry",
    "monitoring.bb.chart.volume.description":
      "Every webhook, all branches, from the stored metrics of the selected data source.",
    "monitoring.bb.chart.outcomes.title": "Item webhooks of branches with a camera",
    "monitoring.bb.chart.outcomes.description":
      "What the backend did with each logged item webhook. Items of branches without a camera are left out.",
    "monitoring.bb.series.order": "Orders",
    "monitoring.bb.series.item": "Items",
    "monitoring.bb.series.status": "Statuses",
    "monitoring.bb.group.captured": "Camera fired",
    "monitoring.bb.group.in_progress": "Not packed yet",
    "monitoring.bb.group.skipped": "Skipped by settings",
    "monitoring.bb.group.no_order": "No order",
    "monitoring.bb.group.failed": "Failed",

    // --- branches -------------------------------------------------------------------------------------
    "monitoring.bb.branches.title": "Branches with a camera",
    "monitoring.bb.branches.description": "Logged webhooks per branch in the window.",
    "monitoring.bb.branches.empty": "No webhook of a branch with a camera in the window.",
    "monitoring.bb.branches.col.branch": "Branch",
    "monitoring.bb.branches.col.orders": "Orders",
    "monitoring.bb.branches.col.items": "Item webhooks",
    "monitoring.bb.branches.col.statuses": "Status updates",
    "monitoring.bb.branches.col.packed": "Packed",
    "monitoring.bb.branches.col.captured": "Camera fired",
    "monitoring.bb.branches.col.missing": "Packed, no trigger",
    "monitoring.bb.branches.col.outcomes": "Other outcomes",
    "monitoring.bb.branches.col.last": "Last webhook",
    "monitoring.bb.other.title": "Branches without a camera",
    "monitoring.bb.other.description":
      "They send orders too; their item webhooks are only counted. {total} branches in the window.",
    "monitoring.bb.other.orders": "{orders} orders",

    // --- webhook log ----------------------------------------------------------------------------------
    "monitoring.bb.log.title": "Incoming webhooks",
    "monitoring.bb.log.description":
      "Logged webhooks, newest first. Open a row for the stored payload and the decision.",
    "monitoring.bb.log.search": "Search pizza, order, item id",
    "monitoring.bb.log.kind": "Kind",
    "monitoring.bb.log.kind.all": "All kinds",
    "monitoring.bb.log.outcome": "Outcome",
    "monitoring.bb.log.outcome.all": "All outcomes",
    "monitoring.bb.log.outcome.problems": "Problems only",
    "monitoring.bb.log.branch": "Branch",
    "monitoring.bb.log.branch.all": "All branches",
    "monitoring.bb.log.empty": "No webhook matches the filter.",
    "monitoring.bb.log.limited": "Showing the newest {limit}; narrow the filter to see older ones.",
    "monitoring.bb.log.loadMore": "Show more",
    "monitoring.bb.log.payload": "Payload",
    "monitoring.bb.log.payloadNone":
      "No payload stored (kept for orders and items of branches with a camera only).",
    "monitoring.bb.log.payloadTruncated": "The payload was cut at 24 kB.",
    "monitoring.bb.log.fields": "Decision",
    "monitoring.bb.log.timeline": "Pizza timeline",
    "monitoring.bb.log.order": "order",
    "monitoring.bb.log.items": "{count} items",
    "monitoring.bb.log.statusChange": "{from} → {to}",
    "monitoring.bb.mobile.title": "Mobile API (tablet app)",
    "monitoring.bb.mobile.description":
      "Every request of the BiteBerry tablet app and of the station tablets to /api/mobile and what the backend answered (kept 14 days).",
    "monitoring.bb.mobile.status": "Status",
    "monitoring.bb.mobile.status.all": "All",
    "monitoring.bb.mobile.search": "Search path, login, pizza id, answer",
    "monitoring.bb.mobile.empty": "No request matches the filter.",
    "monitoring.bb.mobile.request": "Request",
    "monitoring.bb.mobile.response": "Response",
    "monitoring.bb.mobile.headers": "Headers",
    "monitoring.bb.mobile.bytes": "{bytes} B",
    "monitoring.bb.mobile.binary": "Binary content (not kept).",
    "monitoring.bb.mobile.noBody": "No body.",
    "monitoring.bb.mobile.field.received": "Received",
    "monitoring.bb.mobile.field.duration": "Answered in",
    "monitoring.bb.mobile.field.user": "Account",
    "monitoring.bb.mobile.field.scope": "Token scope",
    "monitoring.bb.mobile.field.query": "Query",
    "monitoring.bb.mobile.field.ip": "IP",
    "monitoring.bb.mobile.field.userAgent": "User agent",

    // --- kinds / outcomes -----------------------------------------------------------------------------
    "monitoring.bb.kind.order": "Order",
    "monitoring.bb.kind.item": "Item",
    "monitoring.bb.kind.status": "Status",
    "monitoring.bb.outcome.stored": "stored",
    "monitoring.bb.outcome.no_pizzas": "no pizza in the order",
    "monitoring.bb.outcome.acquisition_disabled": "photos off",
    "monitoring.bb.outcome.error": "error",
    "monitoring.bb.outcome.not_pizza": "not a pizza",
    "monitoring.bb.outcome.other_branch": "branch without camera",
    "monitoring.bb.outcome.no_order": "no order",
    "monitoring.bb.outcome.in_progress": "not packed yet",
    "monitoring.bb.outcome.no_packing_step": "no packing step",
    "monitoring.bb.outcome.branch_not_found": "branch not found",
    "monitoring.bb.outcome.bb_trigger_disabled": "app fires the camera",
    "monitoring.bb.outcome.no_camera": "no camera",
    "monitoring.bb.outcome.trigger_failed": "trigger failed",
    "monitoring.bb.outcome.device_error": "camera error",
    "monitoring.bb.outcome.captured": "camera fired",
    "monitoring.bb.outcome.updated": "updated",
    "monitoring.bb.outcome.unchanged": "unchanged",
    "monitoring.bb.outcome.order_not_found": "order not stored",
    "monitoring.bb.outcome.missing_order_id": "no order id",
    "monitoring.bb.outcome.unknown": "unknown",

    // --- fields ----------------------------------------------------------------------------------------
    "monitoring.bb.field.received": "Received",
    "monitoring.bb.field.sentAt": "Sent by BB",
    "monitoring.bb.field.eventType": "Event",
    "monitoring.bb.field.processing": "Processing",
    "monitoring.bb.field.item": "Item",
    "monitoring.bb.field.order": "Order",
    "monitoring.bb.field.branch": "Branch",
    "monitoring.bb.field.steps": "Steps",
    "monitoring.bb.field.camera": "Camera",
    "monitoring.bb.field.trigger": "Trigger RPC",
    "monitoring.bb.field.captureCount": "Capture #",
    "monitoring.bb.field.pizzaKnown": "Pizza document",
    "monitoring.bb.field.pizzaKnown.yes": "existed",
    "monitoring.bb.field.pizzaKnown.no": "missing",
    "monitoring.bb.field.pizzas": "Pizzas stored",
    "monitoring.bb.field.split": "Lines split",
    "monitoring.bb.field.status": "Status",
    "monitoring.bb.field.detail": "Detail",
    "monitoring.bb.field.instance": "Instance",

    // --- pizza timeline -----------------------------------------------------------------------------
    "monitoring.bb.timeline.title": "Pizza timeline",
    "monitoring.bb.timeline.description": "Order, pieces, every logged webhook and monitoring event of the pizza.",
    "monitoring.bb.timeline.noOrder": "No stored order contains this item.",
    "monitoring.bb.timeline.order": "Order",
    "monitoring.bb.timeline.orderItems": "Lines of the order",
    "monitoring.bb.timeline.pieces": "Stored pizzas",
    "monitoring.bb.timeline.noPieces": "No pizza document for this item.",
    "monitoring.bb.timeline.events": "What happened",
    "monitoring.bb.timeline.empty": "Nothing logged for this pizza.",
    "monitoring.bb.timeline.cooked": "cooked",
    "monitoring.bb.timeline.packed": "packed",
    "monitoring.bb.timeline.captured": "captured",
    "monitoring.bb.timeline.notCaptured": "no capture",
    "monitoring.bb.timeline.photo": "photo received",
    "monitoring.bb.timeline.noPhoto": "no photo",
    "monitoring.bb.timeline.evaluated": "evaluated",
    "monitoring.bb.timeline.milestone.cooked": "Cooking done (stored flag)",
    "monitoring.bb.timeline.milestone.packed": "Packing done (stored flag)",
    "monitoring.bb.timeline.milestone.captured": "Capture stored",
    "monitoring.bb.timeline.milestone.evaluated": "Verdict stored",
    "monitoring.bb.timeline.event": "Monitoring event",
    "monitoring.bb.timeline.webhooks": "{count} logged webhooks",
    "monitoring.bb.timeline.openPizza": "Pizza detail",
  },
  cs: {
    "monitoring.tab.bb": "BiteBerry",

    // --- summary -------------------------------------------------------------------------------------
    "monitoring.bb.summary.coverage": "Vyfocené hotové pizzy",
    "monitoring.bb.summary.coverageHint":
      "Pizzy objednávek poboček, které fotí, dokončené v BiteBerry (zabalené, nebo objednávka prošla kuchyní) v okně (nejvýš 7 dní)",
    "monitoring.bb.summary.coverageDetail": "{captured} z {finished} · {missing} bez fotky",
    "monitoring.bb.summary.packed": "Nahlášené zabalení",
    "monitoring.bb.summary.packedHint":
      "Pizzy, u kterých item webhook nahlásil krok Balení jako hotový, na pobočkách, které fotí",
    "monitoring.bb.summary.packedDetail": "{captured} spustilo kameru · {missing} ne",
    "monitoring.bb.summary.received": "Přijaté webhooky",
    "monitoring.bb.summary.receivedHint":
      "Všechny webhooky z BiteBerry v okně, všechny pobočky (počítají je backendové instance zvoleného zdroje dat)",
    "monitoring.bb.summary.receivedDetail": "{orders} objednávek · {items} položek · {statuses} stavů",
    "monitoring.bb.summary.unmatched": "Položky bez objednávky",
    "monitoring.bb.summary.unmatchedHint":
      "Item webhooky pizz, k nimž jsme v tu chvíli neměli uloženou objednávku: ztracená objednávka, item webhook dřív než objednávka, nebo pobočka bez kamery, jejíž objednávku tato instance neviděla",
    "monitoring.bb.summary.unmatchedDetail": "různých id položek",
    "monitoring.bb.summary.other": "Pobočky bez kamery",
    "monitoring.bb.summary.otherHint": "Pobočky, které posílají objednávky, s vypnutým focením (enable_acquisition)",
    "monitoring.bb.summary.otherDetail": "{orders} objednávek",
    "monitoring.bb.summary.log": "Log webhooků",
    "monitoring.bb.summary.logHint":
      "Zalogované webhooky se drží {days} dní. Starší pizzy se vysvětlují jen z jejich uložených příznaků.",
    "monitoring.bb.summary.logKept": "první webhook · drží se {days} dní",
    "monitoring.bb.summary.logEmpty": "zatím nic",
    "monitoring.bb.summary.logMemory": "paměť této instance (ukládání vypnuto)",

    // --- uncaptured ----------------------------------------------------------------------------------
    "monitoring.bb.uncaptured.title": "Hotové pizzy bez fotky",
    "monitoring.bb.uncaptured.description":
      "Pizzy, které BiteBerry dokončilo (zabalené, nebo objednávka prošla kuchyní), a nemají čas focení. U každé je nejpravděpodobnější důvod podle pizzy a všech webhooků, které k ní přišly.",
    "monitoring.bb.uncaptured.empty": "Každá hotová pizza má fotku.",
    "monitoring.bb.uncaptured.emptyFilter": "Žádná pizza s tímto důvodem.",
    "monitoring.bb.uncaptured.all": "Všechny důvody",
    "monitoring.bb.uncaptured.packedAt": "zabaleno",
    "monitoring.bb.uncaptured.orderedAt": "objednáno",
    "monitoring.bb.uncaptured.orderStatus": "Objednávka",
    "monitoring.bb.uncaptured.hooks": "Item webhooky",
    "monitoring.bb.uncaptured.packedHooks": "zabaleno v {count}",
    "monitoring.bb.uncaptured.lastSteps": "Poslední kroky",
    "monitoring.bb.uncaptured.preLog": "před logem",
    "monitoring.bb.uncaptured.preLogHint":
      "Objednávka je starší než log webhooků; důvod vychází jen z uložených příznaků pizzy",
    "monitoring.bb.uncaptured.hasPhoto": "fotka existuje",
    "monitoring.bb.uncaptured.timeline": "Časová osa",
    "monitoring.bb.uncaptured.more": "Zobrazeno {shown} z {total}",

    // --- reasons -------------------------------------------------------------------------------------
    "monitoring.bb.reason.capture_time_lost": "Fotka je, chybí čas focení",
    "monitoring.bb.reason.capture_time_lost.hint":
      "Kamera fotila a pizza má hodnocení, ale capture_at zmizel: pipeline ukládá celý dokument pizzy, který načetla dřív, než se uložilo focení. Seznamy řazené podle času focení pak pizzu nezobrazí.",
    "monitoring.bb.reason.split_item": "Kus položky objednané vícekrát",
    "monitoring.bb.reason.split_item.hint":
      "Pizzu objednanou v počtu 2+ ukládáme jako X:0, X:1, …, ale BiteBerry hlásí zabalení pod položkou X. Webhook nenajde objednávku a kamera se nespustí.",
    "monitoring.bb.reason.no_order": "Item webhook bez objednávky",
    "monitoring.bb.reason.no_order.hint":
      "BiteBerry nahlásilo zabalení, ale v tu chvíli žádná uložená objednávka položku neobsahovala (item webhook přišel dřív než objednávka, nebo se objednávka neuložila).",
    "monitoring.bb.reason.not_pizza": "Nerozpoznáno jako pizza",
    "monitoring.bb.reason.not_pizza.hint": "Kontrola menu položku při nahlášení zabalení odmítla.",
    "monitoring.bb.reason.branch_not_found": "Pobočka nenalezena",
    "monitoring.bb.reason.branch_not_found.hint": "Pobočka objednávky není v seznamu poboček.",
    "monitoring.bb.reason.acquisition_disabled": "Focení na pobočce vypnuto",
    "monitoring.bb.reason.acquisition_disabled.hint":
      "Pobočka měla při nahlášení zabalení vypnuté enable_acquisition.",
    "monitoring.bb.reason.bb_trigger_disabled": "Kameru spouští aplikace",
    "monitoring.bb.reason.bb_trigger_disabled.hint":
      "Pobočka má vypnutý spouštěč z BiteBerry: fotí obsluha v aplikaci, zabalení kameru nespouští.",
    "monitoring.bb.reason.no_camera": "Pobočka nemá kameru",
    "monitoring.bb.reason.no_camera.hint": "Zabalení přišlo, ale pobočka nemá přiřazenou kameru.",
    "monitoring.bb.reason.trigger_failed": "Spuštění kamery selhalo",
    "monitoring.bb.reason.trigger_failed.hint": "RPC na kameru přes ThingsBoard selhalo nebo neuspělo.",
    "monitoring.bb.reason.device_error": "Kamera hlásila chybu",
    "monitoring.bb.reason.device_error.hint": "Kamera odpověděla na spuštění chybou.",
    "monitoring.bb.reason.error": "Chyba zpracování",
    "monitoring.bb.reason.error.hint": "Zpracování webhooku spadlo na výjimce (viz detail a události).",
    "monitoring.bb.reason.capture_not_stored": "Kamera spuštěna, focení neuloženo",
    "monitoring.bb.reason.capture_not_stored.hint":
      "Webhook se zabalením kameru spustil, ale pizza nemá čas focení a fotka nepřišla.",
    "monitoring.bb.reason.packed_not_logged": "Zabaleno, webhook není v logu",
    "monitoring.bb.reason.packed_not_logged.hint":
      "Pizza má příznak zabalení, ale webhook se zabalením v logu není (přišel před začátkem logu, nebo ho zpracovala jiná instance).",
    "monitoring.bb.reason.never_packed": "Zabalení nikdy nepřišlo",
    "monitoring.bb.reason.never_packed.hint":
      "Item webhooky chodily (např. Vaření hotovo), ale krok Balení nikdy neklesl na 0.",
    "monitoring.bb.reason.no_item_webhook": "Žádný item webhook",
    "monitoring.bb.reason.no_item_webhook.hint":
      "K této pizze nepřišel vůbec žádný item webhook: BiteBerry její kroky nenahlásilo, nebo webhook nedorazil.",

    // --- charts ---------------------------------------------------------------------------------------
    "monitoring.bb.chart.volume.title": "Webhooky z BiteBerry",
    "monitoring.bb.chart.volume.description":
      "Všechny webhooky ze všech poboček, z uložených metrik zvoleného zdroje dat.",
    "monitoring.bb.chart.outcomes.title": "Item webhooky poboček s kamerou",
    "monitoring.bb.chart.outcomes.description":
      "Co backend udělal s každým zalogovaným item webhookem. Položky poboček bez kamery tu nejsou.",
    "monitoring.bb.series.order": "Objednávky",
    "monitoring.bb.series.item": "Položky",
    "monitoring.bb.series.status": "Stavy",
    "monitoring.bb.group.captured": "Kamera spuštěna",
    "monitoring.bb.group.in_progress": "Ještě nezabaleno",
    "monitoring.bb.group.skipped": "Přeskočeno nastavením",
    "monitoring.bb.group.no_order": "Bez objednávky",
    "monitoring.bb.group.failed": "Selhalo",

    // --- branches -------------------------------------------------------------------------------------
    "monitoring.bb.branches.title": "Pobočky s kamerou",
    "monitoring.bb.branches.description": "Zalogované webhooky po pobočkách v okně.",
    "monitoring.bb.branches.empty": "V okně nepřišel žádný webhook pobočky s kamerou.",
    "monitoring.bb.branches.col.branch": "Pobočka",
    "monitoring.bb.branches.col.orders": "Objednávky",
    "monitoring.bb.branches.col.items": "Item webhooky",
    "monitoring.bb.branches.col.statuses": "Změny stavu",
    "monitoring.bb.branches.col.packed": "Zabaleno",
    "monitoring.bb.branches.col.captured": "Kamera spuštěna",
    "monitoring.bb.branches.col.missing": "Zabaleno, bez spuštění",
    "monitoring.bb.branches.col.outcomes": "Další výsledky",
    "monitoring.bb.branches.col.last": "Poslední webhook",
    "monitoring.bb.other.title": "Pobočky bez kamery",
    "monitoring.bb.other.description":
      "Také posílají objednávky; jejich item webhooky se jen počítají. V okně {total} poboček.",
    "monitoring.bb.other.orders": "{orders} objednávek",

    // --- webhook log ----------------------------------------------------------------------------------
    "monitoring.bb.log.title": "Příchozí webhooky",
    "monitoring.bb.log.description":
      "Zalogované webhooky, nejnovější nahoře. Po rozkliknutí řádku uložený payload a rozhodnutí backendu.",
    "monitoring.bb.log.search": "Hledat pizzu, objednávku, id položky",
    "monitoring.bb.log.kind": "Druh",
    "monitoring.bb.log.kind.all": "Všechny druhy",
    "monitoring.bb.log.outcome": "Výsledek",
    "monitoring.bb.log.outcome.all": "Všechny výsledky",
    "monitoring.bb.log.outcome.problems": "Jen problémy",
    "monitoring.bb.log.branch": "Pobočka",
    "monitoring.bb.log.branch.all": "Všechny pobočky",
    "monitoring.bb.log.empty": "Filtru neodpovídá žádný webhook.",
    "monitoring.bb.log.limited": "Zobrazeno nejnovějších {limit}; starší uvidíte po zúžení filtru.",
    "monitoring.bb.log.loadMore": "Zobrazit další",
    "monitoring.bb.log.payload": "Payload",
    "monitoring.bb.log.payloadNone":
      "Payload se neukládá (drží se jen u objednávek a položek poboček s kamerou).",
    "monitoring.bb.log.payloadTruncated": "Payload byl oříznut na 24 kB.",
    "monitoring.bb.log.fields": "Rozhodnutí",
    "monitoring.bb.log.timeline": "Časová osa pizzy",
    "monitoring.bb.log.order": "objednávka",
    "monitoring.bb.log.items": "{count} položek",
    "monitoring.bb.log.statusChange": "{from} → {to}",
    "monitoring.bb.mobile.title": "Mobilní API (aplikace na tabletu)",
    "monitoring.bb.mobile.description":
      "Každý požadavek aplikace BiteBerry na tabletu a tabletů stanic na /api/mobile a co backend odpověděl (uchováno 14 dní).",
    "monitoring.bb.mobile.status": "Stav",
    "monitoring.bb.mobile.status.all": "Vše",
    "monitoring.bb.mobile.search": "Hledat cestu, účet, id pizzy, odpověď",
    "monitoring.bb.mobile.empty": "Filtru neodpovídá žádný požadavek.",
    "monitoring.bb.mobile.request": "Požadavek",
    "monitoring.bb.mobile.response": "Odpověď",
    "monitoring.bb.mobile.headers": "Hlavičky",
    "monitoring.bb.mobile.bytes": "{bytes} B",
    "monitoring.bb.mobile.binary": "Binární obsah (neukládá se).",
    "monitoring.bb.mobile.noBody": "Bez těla.",
    "monitoring.bb.mobile.field.received": "Přijato",
    "monitoring.bb.mobile.field.duration": "Odpověď za",
    "monitoring.bb.mobile.field.user": "Účet",
    "monitoring.bb.mobile.field.scope": "Rozsah tokenu",
    "monitoring.bb.mobile.field.query": "Query",
    "monitoring.bb.mobile.field.ip": "IP",
    "monitoring.bb.mobile.field.userAgent": "User agent",

    // --- kinds / outcomes -----------------------------------------------------------------------------
    "monitoring.bb.kind.order": "Objednávka",
    "monitoring.bb.kind.item": "Položka",
    "monitoring.bb.kind.status": "Stav",
    "monitoring.bb.outcome.stored": "uloženo",
    "monitoring.bb.outcome.no_pizzas": "bez pizzy",
    "monitoring.bb.outcome.acquisition_disabled": "focení vypnuto",
    "monitoring.bb.outcome.error": "chyba",
    "monitoring.bb.outcome.not_pizza": "není pizza",
    "monitoring.bb.outcome.other_branch": "pobočka bez kamery",
    "monitoring.bb.outcome.no_order": "bez objednávky",
    "monitoring.bb.outcome.in_progress": "ještě nezabaleno",
    "monitoring.bb.outcome.no_packing_step": "bez kroku Balení",
    "monitoring.bb.outcome.branch_not_found": "pobočka nenalezena",
    "monitoring.bb.outcome.bb_trigger_disabled": "kameru spouští aplikace",
    "monitoring.bb.outcome.no_camera": "bez kamery",
    "monitoring.bb.outcome.trigger_failed": "spuštění selhalo",
    "monitoring.bb.outcome.device_error": "chyba kamery",
    "monitoring.bb.outcome.captured": "kamera spuštěna",
    "monitoring.bb.outcome.updated": "aktualizováno",
    "monitoring.bb.outcome.unchanged": "beze změny",
    "monitoring.bb.outcome.order_not_found": "objednávka neuložena",
    "monitoring.bb.outcome.missing_order_id": "bez id objednávky",
    "monitoring.bb.outcome.unknown": "neznámé",

    // --- fields ----------------------------------------------------------------------------------------
    "monitoring.bb.field.received": "Přijato",
    "monitoring.bb.field.sentAt": "Odesláno z BB",
    "monitoring.bb.field.eventType": "Událost",
    "monitoring.bb.field.processing": "Zpracování",
    "monitoring.bb.field.item": "Položka",
    "monitoring.bb.field.order": "Objednávka",
    "monitoring.bb.field.branch": "Pobočka",
    "monitoring.bb.field.steps": "Kroky",
    "monitoring.bb.field.camera": "Kamera",
    "monitoring.bb.field.trigger": "RPC spuštění",
    "monitoring.bb.field.captureCount": "Focení č.",
    "monitoring.bb.field.pizzaKnown": "Dokument pizzy",
    "monitoring.bb.field.pizzaKnown.yes": "existoval",
    "monitoring.bb.field.pizzaKnown.no": "chyběl",
    "monitoring.bb.field.pizzas": "Uložené pizzy",
    "monitoring.bb.field.split": "Rozdělené položky",
    "monitoring.bb.field.status": "Stav",
    "monitoring.bb.field.detail": "Detail",
    "monitoring.bb.field.instance": "Instance",

    // --- pizza timeline -----------------------------------------------------------------------------
    "monitoring.bb.timeline.title": "Časová osa pizzy",
    "monitoring.bb.timeline.description": "Objednávka, kusy, každý zalogovaný webhook a události monitoringu k pizze.",
    "monitoring.bb.timeline.noOrder": "Žádná uložená objednávka tuto položku neobsahuje.",
    "monitoring.bb.timeline.order": "Objednávka",
    "monitoring.bb.timeline.orderItems": "Položky objednávky",
    "monitoring.bb.timeline.pieces": "Uložené pizzy",
    "monitoring.bb.timeline.noPieces": "K položce není dokument pizzy.",
    "monitoring.bb.timeline.events": "Co se dělo",
    "monitoring.bb.timeline.empty": "K pizze není nic zalogováno.",
    "monitoring.bb.timeline.cooked": "upečeno",
    "monitoring.bb.timeline.packed": "zabaleno",
    "monitoring.bb.timeline.captured": "vyfoceno",
    "monitoring.bb.timeline.notCaptured": "bez focení",
    "monitoring.bb.timeline.photo": "fotka přišla",
    "monitoring.bb.timeline.noPhoto": "bez fotky",
    "monitoring.bb.timeline.evaluated": "vyhodnoceno",
    "monitoring.bb.timeline.milestone.cooked": "Vaření hotovo (uložený příznak)",
    "monitoring.bb.timeline.milestone.packed": "Balení hotovo (uložený příznak)",
    "monitoring.bb.timeline.milestone.captured": "Focení uloženo",
    "monitoring.bb.timeline.milestone.evaluated": "Verdikt uložen",
    "monitoring.bb.timeline.event": "Událost monitoringu",
    "monitoring.bb.timeline.webhooks": "{count} zalogovaných webhooků",
    "monitoring.bb.timeline.openPizza": "Detail pizzy",
  },
});
